/**
 * ZoeHarlowSection — AI Digital Twin character promo block
 * Displays Zoe Harlow (OhAPI character 8857) with a chat CTA.
 * Placed under the AWTubePlayer on the home page.
 *
 * Zoe Live Embed V1 (additive, flag-gated): adds a "Watch Zoe Live" CTA that
 * opens the reusable AICreatorLiveModal (Trulience iframe). Rendered ONLY when
 * REACT_APP_ENABLE_ZOE_LIVE=true. Existing "Chat with Zoe" (text funnel) and
 * "More Photos" (gallery) are unchanged.
 */
import React, { useState } from "react";
import { Box, Typography, Button, Chip, Modal, IconButton } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import VideocamIcon from "@mui/icons-material/Videocam";
import ZoeChatModal from "./ZoeChatModal";
import AICreatorLiveModal from "./ai/AICreatorLiveModal";
import { getCreator, ZOE_LIVE_ENABLED } from "../config/aiCreators";
import { trackAICreator } from "../utils/aiCreatorAnalytics";

// All generated scenes from OhAPI character 8857
const ZOE_PHOTOS = [
  { src: "/zoe-harlow.webp",              label: "Outdoor" },
  { src: "/zoe-harlow-digital-twin-001.webp", label: "Webcam Studio" },
  { src: "/zoe-bedroom.webp",             label: "Bedroom" },
  { src: "/zoe-rooftop-pool.webp",        label: "Rooftop Pool" },
  { src: "/zoe-gym.webp",                 label: "Gym" },
  { src: "/zoe-hotel-suite.webp",         label: "Hotel Suite" },
];

function ZoeGalleryModal({ onClose }) {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx((i) => (i - 1 + ZOE_PHOTOS.length) % ZOE_PHOTOS.length);
  const next = () => setIdx((i) => (i + 1) % ZOE_PHOTOS.length);
  return (
    <Modal open onClose={onClose}>
      <Box sx={{
        position: "fixed", inset: 0, bgcolor: "rgba(0,0,0,0.92)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        zIndex: 9999, px: 2,
      }} onClick={(e) => e.target === e.currentTarget && onClose()}>
        {/* Close */}
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16, color: "#fff", bgcolor: "rgba(255,255,255,0.1)" }}>
          <CloseIcon />
        </IconButton>
        {/* Counter */}
        <Typography sx={{ color: "#aaa", fontSize: "0.75rem", mb: 1 }}>
          {idx + 1} / {ZOE_PHOTOS.length} — {ZOE_PHOTOS[idx].label}
        </Typography>
        {/* Image + arrows */}
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1, maxHeight: "80vh" }}>
          <IconButton onClick={prev} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Box component="img" src={ZOE_PHOTOS[idx].src} alt={ZOE_PHOTOS[idx].label}
            sx={{ maxHeight: "75vh", maxWidth: "80vw", borderRadius: 2, objectFit: "contain", boxShadow: "0 8px 48px rgba(0,0,0,0.8)" }}
          />
          <IconButton onClick={next} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
        {/* Thumbnails */}
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap", justifyContent: "center" }}>
          {ZOE_PHOTOS.map((p, i) => (
            <Box key={i} component="img" src={p.src} alt={p.label}
              onClick={() => setIdx(i)}
              sx={{
                width: 52, height: 52, objectFit: "cover", borderRadius: 1, cursor: "pointer",
                border: i === idx ? "2px solid #e91e8c" : "2px solid transparent",
                opacity: i === idx ? 1 : 0.55,
                transition: "all 0.15s",
                "&:hover": { opacity: 1 },
              }}
            />
          ))}
        </Box>
      </Box>
    </Modal>
  );
}

const ZOE_IMAGE = "/zoe-harlow.webp";

// Character scenes — generated from OhAPI character 8857
const SCENES = [
  { src: "/zoe-harlow.webp",         label: "Outdoor" },
];

export default function ZoeHarlowSection() {
  const [hovered, setHovered] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);

  const openLive = () => {
    trackAICreator("ai_creator_cta_clicked", {
      creator_id: "zoe",
      provider: "trulience",
      cta: "watch_live",
      surface: "homepage",
    });
    setLiveOpen(true);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800,
        mb: 3,
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #1a0a2e 0%, #0d0d1a 60%, #1a0a1a 100%)",
        border: "1px solid rgba(233,30,140,0.25)",
        boxShadow: "0 4px 32px rgba(233,30,140,0.12)",
        position: "relative",
      }}
    >
      {/* AI badge removed — not shown to visitors */}

      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "stretch" }}>
        {/* Image */}
        <Box
          sx={{
            width: { xs: "100%", sm: 220 },
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
            minHeight: { xs: 280, sm: "auto" },
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <Box
            component="img"
            src={ZOE_IMAGE}
            alt="Zoe Harlow — AI Cam Girl"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top center",
              display: "block",
              transform: hovered ? "scale(1.04)" : "scale(1)",
              transition: "transform 0.4s ease",
              minHeight: { xs: 280, sm: 320 },
            }}
          />
          {/* Gradient overlay on image */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: {
                xs: "linear-gradient(to top, #0d0d1a 0%, transparent 50%)",
                sm: "linear-gradient(to right, transparent 60%, #0d0d1a 100%)",
              },
            }}
          />
          {/* Online indicator */}
          <Box sx={{ position: "absolute", bottom: 12, left: 12, display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#4caf50", boxShadow: "0 0 6px #4caf50" }} />
            <Typography sx={{ color: "#fff", fontSize: "0.7rem", fontWeight: 600, textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
              Online Now
            </Typography>
          </Box>
        </Box>

        {/* Info */}
        <Box sx={{ flex: 1, p: { xs: 2.5, sm: 3 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", fontSize: { xs: "1.4rem", sm: "1.6rem" } }}>
              Zoe Harlow
            </Typography>
            <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>24</Typography>
          </Box>

          <Typography sx={{ color: "#e91e8c", fontSize: "0.8rem", fontWeight: 600, mb: 1.5, letterSpacing: 0.5 }}>
            🇨🇦 Canadian · Fitness & Lifestyle Creator
          </Typography>

          <Typography sx={{ color: "#bbb", fontSize: "0.85rem", lineHeight: 1.6, mb: 2 }}>
            Athletic, ambitious, and always online. Zoe combines gym confidence with an irresistible smile.
            Chat with her AI — available 24/7, remembers you, and gets more personal every conversation.
          </Typography>

          {/* Tags */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2.5 }}>
            {["Blonde", "Blue Eyes", "Athletic", "Fitness", "Flirty", "24/7"].map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.07)",
                  color: "#ccc",
                  fontSize: "0.65rem",
                  border: "1px solid rgba(255,255,255,0.1)",
                  height: 22,
                }}
              />
            ))}
          </Box>

          {/* CTAs */}
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {/* Zoe Live Embed V1 — additive, flag-gated. Leaves other CTAs intact. */}
            {ZOE_LIVE_ENABLED && (
              <Button
                variant="contained"
                startIcon={<VideocamIcon />}
                onClick={openLive}
                sx={{
                  background: "linear-gradient(90deg, #e91e8c, #c2185b)",
                  "&:hover": { opacity: 0.9 },
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  borderRadius: 2,
                  textTransform: "none",
                  px: 2.5,
                  py: 1,
                }}
              >
                Watch Zoe Live
              </Button>
            )}
            <Button
              variant={ZOE_LIVE_ENABLED ? "outlined" : "contained"}
              startIcon={<ChatIcon />}
              onClick={() => setChatOpen(true)}
              sx={
                ZOE_LIVE_ENABLED
                  ? {
                      borderColor: "rgba(233,30,140,0.5)",
                      color: "#e91e8c",
                      "&:hover": { borderColor: "#e91e8c", bgcolor: "rgba(233,30,140,0.08)" },
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      borderRadius: 2,
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                    }
                  : {
                      bgcolor: "#e91e8c",
                      "&:hover": { bgcolor: "#c2185b" },
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      borderRadius: 2,
                      textTransform: "none",
                      px: 2.5,
                      py: 1,
                    }
              }
            >
              Chat with Zoe
            </Button>
            <Button
              variant="outlined"
              startIcon={<FavoriteIcon />}
              onClick={() => setGalleryOpen(true)}
              sx={{
                borderColor: "rgba(233,30,140,0.5)",
                color: "#e91e8c",
                "&:hover": { borderColor: "#e91e8c", bgcolor: "rgba(233,30,140,0.08)" },
                fontWeight: 600,
                fontSize: "0.85rem",
                borderRadius: 2,
                textTransform: "none",
                px: 2,
                py: 1,
              }}
            >
              More Photos
            </Button>
          </Box>
        </Box>
      </Box>
      {/* Live avatar modal (Zoe Live Embed V1) */}
      {liveOpen && ZOE_LIVE_ENABLED && (
        <AICreatorLiveModal
          creator={getCreator("zoe")}
          surface="homepage"
          onClose={() => setLiveOpen(false)}
          onFallbackToChat={() => setChatOpen(true)}
        />
      )}
      {/* Chat modal */}
      {chatOpen && <ZoeChatModal onClose={() => setChatOpen(false)} />}
      {/* Gallery modal */}
      {galleryOpen && <ZoeGalleryModal onClose={() => setGalleryOpen(false)} />}
    </Box>
  );
}
